import * as React from "react";
import { useMemo } from "react";
import { useSelector, useStore } from "react-redux";

import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Slide,
} from "@mui/material";

import { InputField } from "../components/InputField.tsx";
import { setSameDaySpend } from "../Store/roiStore.ts";

import "../styles/pages/sameday.scss";

const PCT = "%";
const DOLLAR = "$";

const SPEND_KEYS = {
    annualBillOnlySpend: "Annual Bill-Only Spend",
    pctJoints: "Total Joints (%)",
    pctSpineTrauma: "Spine & Trauma (%)",
    pctEp: "Electrophysiology (%)",
    pctRemaining: "Remaining (%)",
    annualPoVolume: "Annual Bill-Only PO Volume",
    uniqueSuppliers: "Est. Unique Bill-Only Suppliers",
    pctNoErpId: "% Bill-Only Items Without ERP Item ID",
    pctPricingAccuracyNoErp: "% Pricing Accuracy for Bill-Only Items w/o ERP Item File ID",
    pctSpendOffContract: "% Bill-Only Spend Off Contract",
    pctOffContractPricingAccuracy: "Off Contract Spend Pricing Accuracy",
    pctChargeCaptureAccuracy: "Est. Bill-Only Charge Capture Rate",
    jointsCapitationAgreed: "Joint Vendors Agreed to Construct/Capitation",
    pctJointsCapitation: "% Total Joint POs Construct/Capitation",
    epCapitationAgreed: "EP Vendors Agreed to Construct/Capitation",
    pctEpCapitation: "% EP POs Construct/Capitation",
    weeklyHoursImplantCoordinators: "Weekly Hours - Implant Coordinators",
    weeklyHoursBuyers: "Weekly Hours - Buyers",
    weeklyHoursAccounting: "Weekly Hours - Accounting Staff",
    fteWage: "FTE Wage ($/hr)",
    pctPosNeedingRework: "% Bill-Only POs Requiring Change Orders or Rework",
    pctEmrAligned: "% Bill-Only Requests Aligned with EMR Documentation 100%",
} as const;

export { SPEND_KEYS as SAMEDAY_SPEND_KEYS };

export const SameDaySpendDetails = () => {
    const sameDay = useSelector((state: any) => state.inputFields.sameDay) ?? {};
    const annualNpsr = useSelector(
        (state: any) =>
            Number(state.inputFields.client?.annualNetPatientServiceRevenue ?? 0)
    );
    const isSameDayOnly = !!sameDay.isSameDayOnly;
    const spend = sameDay.spend ?? {};
    const { dispatch } = useStore();

    const billOnlyEstimate = useMemo(
        () => annualNpsr * 0.13 * 0.2,
        [annualNpsr]
    );

    const update = (name: string, value: any) =>
        dispatch(setSameDaySpend({ name, value }));

    const numericInput = (
        name: string,
        label: string,
        opts: {
            prefix?: string;
            suffix?: string;
            placeholder?: string;
            // helperText renders BELOW the input and wraps naturally; prefer this
            // over `placeholder` for any description longer than ~30 chars,
            // because in-field placeholders get clipped at the input's right edge.
            helperText?: string;
        } = {}
    ) => (
        <InputField
            placeholder={opts.placeholder ?? label}
            helperText={opts.helperText}
            label={label}
            value={spend[name] ?? ""}
            type="number"
            prefix={opts.prefix}
            suffix={opts.suffix}
            onValueInputChange={(_n, value) => update(name, value)}
        />
    );

    const yesNoInput = (
        name: string,
        label: string
    ) => (
        <div>
            <div className="sameday-spend-yesno-label">{label}</div>
            <RadioGroup
                row
                value={spend[name] ?? ""}
                onChange={(event) => update(name, event.target.value)}
            >
                <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
            </RadioGroup>
        </div>
    );

    const splitTotal =
        Number(spend[SPEND_KEYS.pctJoints] ?? 0) +
        Number(spend[SPEND_KEYS.pctSpineTrauma] ?? 0) +
        Number(spend[SPEND_KEYS.pctEp] ?? 0) +
        Number(spend[SPEND_KEYS.pctRemaining] ?? 0);
    const mixIsValid = Math.abs(splitTotal - 100) < 0.01;

    return (
        <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <div className="sameday-spend-details-page">
                <h1>PAR SameDay Spend Details</h1>
                <p className="sameday-spend-details-instructions">
                    PAR SameDay is licensed for all facilities, so volumes and activity
                    are aggregated here rather than entered per facility.
                    {annualNpsr > 0 && (
                        <>
                            {" "}Suggested Annual Bill-Only Spend (NPSR × 13% × 20%):{" "}
                            <strong>
                                ${billOnlyEstimate.toLocaleString("en-US", {
                                    maximumFractionDigits: 0,
                                })}
                            </strong>
                            .
                        </>
                    )}
                </p>

                <div className="sameday-spend-details-section">
                    <h3>Spend</h3>
                    <div className="sameday-spend-details-grid">
                        {numericInput(SPEND_KEYS.annualBillOnlySpend, "Annual Bill-Only Spend", {
                            prefix: DOLLAR,
                        })}
                        {numericInput(SPEND_KEYS.annualPoVolume, "Annual Bill-Only PO Volume")}
                        {numericInput(
                            SPEND_KEYS.uniqueSuppliers,
                            "Est. Unique Bill-Only Suppliers"
                        )}
                    </div>
                </div>

                <div className="sameday-spend-details-section">
                    <h3>
                        Bill-Only Spend Mix{" "}
                        <span
                            className={
                                mixIsValid
                                    ? "sameday-spend-details-mix-ok"
                                    : "sameday-spend-details-mix-warn"
                            }
                        >
                            (sum: {splitTotal.toFixed(0)}%)
                        </span>
                    </h3>
                    <div className="sameday-spend-details-grid">
                        {numericInput(SPEND_KEYS.pctJoints, "Total Joints", { suffix: PCT })}
                        {numericInput(SPEND_KEYS.pctSpineTrauma, "Spine & Trauma", {
                            suffix: PCT,
                        })}
                        {numericInput(SPEND_KEYS.pctEp, "Electrophysiology", {
                            suffix: PCT,
                        })}
                        {numericInput(
                            SPEND_KEYS.pctRemaining,
                            "Remaining (plastics, shoulder, ocular, etc.)",
                            { suffix: PCT }
                        )}
                    </div>
                    {!mixIsValid && (
                        <p className="sameday-spend-details-mix-error">
                            The four bill-only mix categories must add up to exactly
                            100% before you can continue. Current total:{" "}
                            <strong>{splitTotal.toFixed(0)}%</strong>.
                        </p>
                    )}
                </div>

                <div className="sameday-spend-details-section">
                    <h3>Pricing & Contract Accuracy</h3>
                    <div className="sameday-spend-details-grid">
                        {numericInput(
                            SPEND_KEYS.pctNoErpId,
                            "% Bill-Only Items Without ERP Item ID",
                            { suffix: PCT }
                        )}
                        {numericInput(
                            SPEND_KEYS.pctPricingAccuracyNoErp,
                            "% Pricing Accuracy for Bill-Only Items w/o ERP Item File ID",
                            { suffix: PCT }
                        )}
                        {numericInput(
                            SPEND_KEYS.pctSpendOffContract,
                            "% Bill-Only Spend Off Contract",
                            { suffix: PCT }
                        )}
                        {numericInput(
                            SPEND_KEYS.pctOffContractPricingAccuracy,
                            "Off Contract Spend Pricing Accuracy",
                            { suffix: PCT }
                        )}
                        {numericInput(
                            SPEND_KEYS.pctChargeCaptureAccuracy,
                            "Est. Bill-Only Charge Capture Rate",
                            { suffix: PCT }
                        )}
                    </div>
                </div>

                <div className="sameday-spend-details-section">
                    <h3>Contract Complexity</h3>
                    <div className="sameday-spend-details-grid">
                        {yesNoInput(
                            SPEND_KEYS.jointsCapitationAgreed,
                            "Have at least 80% of your total joint vendors agreed to capitations (or constructs) in your contracts?"
                        )}
                        {spend[SPEND_KEYS.jointsCapitationAgreed] === "yes" &&
                            numericInput(
                                SPEND_KEYS.pctJointsCapitation,
                                "Est. % of total joint POs priced as construct/capitation",
                                { suffix: PCT }
                            )}
                        {yesNoInput(
                            SPEND_KEYS.epCapitationAgreed,
                            "Have at least 80% of your EP vendors agreed to capitations (or constructs) in your contracts?"
                        )}
                        {spend[SPEND_KEYS.epCapitationAgreed] === "yes" &&
                            numericInput(
                                SPEND_KEYS.pctEpCapitation,
                                "Est. % of EP POs priced as construct/capitation",
                                { suffix: PCT }
                            )}
                    </div>
                </div>

                <div className="sameday-spend-details-section">
                    <h3>Bill-Only Process Labor</h3>
                    <div className="sameday-spend-details-grid">
                        {numericInput(
                            SPEND_KEYS.weeklyHoursImplantCoordinators,
                            "Weekly hours - Implant Coordinators",
                            {
                                helperText:
                                    "Total hours spent by all implant coordinators on bill-only processes.",
                            }
                        )}
                        {numericInput(
                            SPEND_KEYS.weeklyHoursBuyers,
                            "Weekly hours - Buyers",
                            {
                                helperText:
                                    "Total hours spent by all buyers on bill-only transactions.",
                            }
                        )}
                        {numericInput(
                            SPEND_KEYS.weeklyHoursAccounting,
                            "Weekly hours - Accounting staff",
                            {
                                helperText:
                                    "Total hours spent on bill-only invoice exceptions.",
                            }
                        )}
                        {isSameDayOnly &&
                            numericInput(
                                SPEND_KEYS.fteWage,
                                "Fully-loaded average FTE hourly wage for coordinators, buyers and AP staff",
                                {
                                    prefix: DOLLAR,
                                    suffix: "/hr",
                                }
                            )}
                    </div>
                </div>

                <div className="sameday-spend-details-section">
                    <h3>Process Quality (informational)</h3>
                    <div className="sameday-spend-details-grid">
                        {numericInput(
                            SPEND_KEYS.pctPosNeedingRework,
                            "% Bill-Only POs requiring change orders or rework",
                            {
                                suffix: PCT,
                                helperText:
                                    "Estimate the % of bill-only PO requests that require rework.",
                            }
                        )}
                        {numericInput(SPEND_KEYS.pctEmrAligned, "EMR Match", {
                            suffix: PCT,
                            helperText:
                                "What percentage of bill-only PO requests match the EMR record?",
                        })}
                    </div>
                </div>
            </div>
        </Slide>
    );
};
